import { Alert, AlertDescription, AlertIcon, Box, Button, useToast } from '@chakra-ui/react'
import { FunctionComponent, ReactNode, useEffect } from 'react'
import { FieldPath, FormProvider, useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import { LimitedPrescriberProfile } from '../users/UsersSlice'
import { useHasEntitlement } from '../../pages/OrganizationProfilePage/planEntitlements'
import PatientForm from '../../pages/PatientsPage/PatientForm'
import {
  resetDvaValidationResult,
  resetIhiValidationResult,
  resetMedicareValidationResult,
} from '../../pages/PatientsPage/PatientFormSlice'
import { formatIHIRecord, formatIHIValidate } from '../../pages/PatientsPage/utils'
import { useCreatePatientMutation } from '../../services/patients'
import { FetchBaseQueryErrorResult } from '../../services/baseQuery'
import { Patient } from '../../types'
import { FeatureKey } from '../../types/organization-entitlements'
import { ServerError } from '../../types/result'
import {
  CreatePatientRequest,
  emptyPatient,
  normalizePatientFormData,
} from '../../utils/patient/patientPayload'
import { getMissingPrescriberMessage } from '../patient/patientAccessScope'

export type PatientRegistrationFieldIssues = Partial<
  Record<FieldPath<CreatePatientRequest>, string>
>

interface PatientRegistrationProps {
  initialValues?: Partial<CreatePatientRequest>
  fieldIssues?: PatientRegistrationFieldIssues
  selectedPrescriber: LimitedPrescriberProfile | null
  hidePrescriberSelection?: boolean
  onPrescriberChange?: (id: string) => void
  onCreated?: (patient: Patient) => void
  registerPatient?: (patient: CreatePatientRequest) => Promise<Patient | undefined>
  isSubmitting?: boolean
  onCancel?: () => void
  children?: (state: {
    form: ReactNode
    error?: ServerError
    isLoading: boolean
    submitButton: ReactNode
  }) => ReactNode
}

const PatientRegistration: FunctionComponent<PatientRegistrationProps> = ({
  initialValues,
  fieldIssues,
  selectedPrescriber,
  hidePrescriberSelection = false,
  onPrescriberChange,
  onCreated,
  registerPatient,
  isSubmitting = false,
  onCancel,
  children,
}) => {
  const toast = useToast()
  const dispatch = useAppDispatch()
  const prescriber = useAppSelector((state) => state.prescriber?.prescriber)
  const isSharedPatientsFeatureOn = useHasEntitlement(FeatureKey.SHARED_PATIENTS)
  const [createPatient, createPatientResult] = useCreatePatientMutation()
  const methods = useForm<CreatePatientRequest>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: { ...emptyPatient, ...initialValues } as CreatePatientRequest,
  })
  const { handleSubmit, reset, setError, getValues } = methods
  const { medicareValidationResult, dvaValidationResult, ihiValidationResult } = useAppSelector(
    (state) => state.patientForm
  )
  const effectivePrescriber = hidePrescriberSelection ? prescriber : selectedPrescriber
  const error = (createPatientResult.error as FetchBaseQueryErrorResult['error'])
    ?.data as ServerError

  useEffect(() => {
    Object.entries(fieldIssues ?? {}).forEach(([field, message]) => {
      if (message) setError(field as FieldPath<CreatePatientRequest>, { type: 'server', message })
    })
  }, [fieldIssues, setError])

  useEffect(
    () => () => {
      dispatch(resetMedicareValidationResult())
      dispatch(resetDvaValidationResult())
      dispatch(resetIhiValidationResult())
    },
    [dispatch]
  )

  const resetRegistration = () => {
    reset({ ...emptyPatient, ...initialValues } as CreatePatientRequest)
    dispatch(resetMedicareValidationResult())
    dispatch(resetDvaValidationResult())
    dispatch(resetIhiValidationResult())
  }

  const cancel = () => {
    resetRegistration()
    onCancel?.()
  }

  const submit = async (data: CreatePatientRequest) => {
    const formData = normalizePatientFormData({ ...data })
    if (!isSharedPatientsFeatureOn && !effectivePrescriber?.id) {
      toast({
        title: 'Cannot create patient',
        description: getMissingPrescriberMessage('creating a patient'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    if (!effectivePrescriber?.hpii) {
      toast({
        title: 'Cannot create patient',
        description: 'Selected prescriber does not have a valid HPII number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    const newIHIRecord = formatIHIRecord({
      medicareValidationResult,
      dvaValidationResult,
      ihiValidationResult,
    })
    if (!newIHIRecord) {
      const { ihi_number } = getValues()
      if (
        !ihiValidationResult.service_message?.codeField &&
        ihiValidationResult.ihi_status === '' &&
        ihi_number
      ) {
        setError('ihi_number', { type: 'manual', message: 'Please validate the IHI number' })
        return
      }
      toast({
        position: 'top',
        title: 'Cannot create patient',
        description: 'The patient has an invalid IHI number, Please check it and try again.',
        status: 'error',
        isClosable: true,
      })
      return
    }

    if (!formatIHIValidate(medicareValidationResult).isAcceptable) {
      formData.medicare_no = ''
      formData.medicare_irn = ''
    }
    if (!formatIHIValidate(dvaValidationResult).isAcceptable) {
      formData.dva_file_no = ''
      formData.dva_card_colour = ''
    }

    const patientPayload = { ...formData, ...newIHIRecord }
    if (registerPatient) {
      const patient = await registerPatient(patientPayload)
      if (!patient) return
      onCreated?.(patient)
      resetRegistration()
      return
    }
    const result = await createPatient({
      prescriberId: effectivePrescriber?.id,
      patient: patientPayload,
    })
    if ('data' in result) {
      onCreated?.(result.data)
      resetRegistration()
    }
  }

  const form = (
    <FormProvider {...methods}>
      <PatientForm
        defaultPatient={{ ...emptyPatient, ...initialValues }}
        selectedPrescriber={selectedPrescriber}
        onPrescriberChange={onPrescriberChange}
        hidePrescriberSelection={hidePrescriberSelection}
        isSharedPatientsFeatureOn={isSharedPatientsFeatureOn}
      />
    </FormProvider>
  )
  const submitButton = (
    <Button
      colorScheme="blue"
      type="submit"
      isLoading={createPatientResult.isLoading || isSubmitting}
    >
      Create
    </Button>
  )

  return (
    <Box as="form" display="contents" onSubmit={handleSubmit(submit)}>
      {children ? (
        children({
          form,
          error,
          isLoading: createPatientResult.isLoading || isSubmitting,
          submitButton,
        })
      ) : (
        <>
          {form}
          {error && (
            <Alert status="error" mt={3}>
              <AlertIcon />
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}
          <Box display="flex" gap={3} justifyContent="flex-end" mt={4}>
            {onCancel && <Button onClick={cancel}>Cancel</Button>}
            {submitButton}
          </Box>
        </>
      )}
    </Box>
  )
}

export default PatientRegistration
